import { Box, useTheme } from '@mui/material';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { getFormattedDate } from '@/lib/dateFormatting';
import { Icon, Row, Text } from '@/components/ui';
import { moneyIcon } from '@/assets';

import type { Site } from '../types';

interface Props {
  site: Site;
  handleClick: () => void;
}

export const SiteCard: FC<Props> = ({ site, handleClick }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const currencyFormat = (value: number) => {
    return value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  return (
    <Box
      sx={{
        mt: theme.spacing(1),
        padding: theme.spacing(1),
        border: '1px solid',
        borderRadius: theme.shape.borderRadius,
        background: theme.palette.background.paper,
      }}
      onClick={handleClick}
    >
      <Box>
        <Row sx={{ justifyContent: 'space-between' }}>
          <Box
            sx={{
              maxWidth: {
                xs: `calc(100% - ${theme.spacing(10)})`,
                sm: `calc(100% - ${theme.spacing(15)})`,
                md: `calc(100% - ${theme.spacing(20)})`,
              },
            }}
          >
            <Text
              sx={{
                color: theme.palette.text.primary,
                fontWeight: 'bold',
                overflow: 'hidden',
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                hyphens: 'auto',
                lineHeight: 1.2,
                display: '-webkit-box',
                WebkitLineClamp: { xs: 3, sm: 2 },
                WebkitBoxOrient: 'vertical',
                fontSize: {
                  xs: theme.typography.body2.fontSize,
                  sm: theme.typography.body1.fontSize,
                },
              }}
            >
              {site.siteName}
            </Text>
          </Box>
          <Box
            sx={{
              mx: theme.spacing(0.5),
              maxWidth: {
                xs: `calc(100% - ${theme.spacing(8)})`,
                sm: `calc(100% - ${theme.spacing(12)})`,
              },
              flex: 1,
            }}
          >
            <Row spacing={theme.spacing(1)}>
              <Text
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: theme.typography.body2.fontSize,
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                {t('creation_date_sites_page')}
              </Text>
              <Text
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: theme.typography.body2.fontSize,
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                }}
              >
                {getFormattedDate(site.creationDate)}
              </Text>
            </Row>
          </Box>
          <Box
            sx={{
              maxWidth: {
                xs: `calc(100% - ${theme.spacing(6)})`,
                sm: `calc(100% - ${theme.spacing(10)})`,
                md: `calc(100% - ${theme.spacing(12)})`,
              },
            }}
          >
            <Text
              sx={{
                color: theme.palette.text.primary,
                fontWeight: '500',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                mx: theme.spacing(0.5),
                fontSize: {
                  xs: theme.typography.caption.fontSize,
                  sm: theme.typography.body2.fontSize,
                },
              }}
            >
              {site.totalPrice ? currencyFormat(site.totalPrice) : '0.00'}
            </Text>
          </Box>
          <Icon src={moneyIcon} sx={{ mx: 0 }} />
        </Row>
        <Row spacing={theme.spacing(1)} sx={{ mt: theme.spacing(1) }}>
          {' '}
          {/* 8px, 8px */}
          <Box
            sx={{
              backgroundColor: theme.palette.primary.main,
              borderRadius: theme.spacing(2.5),
              padding: `${theme.spacing(0.25)} ${theme.spacing(1.5)}`,
            }}
          >
            <Row spacing={theme.spacing(1)}>
              <Text
                sx={{
                  fontSize: theme.typography.body2.fontSize,
                  color: 'white',
                }}
              >
                {t('priority_sites_page')}
              </Text>
              <Text
                sx={{
                  fontSize: theme.typography.body2.fontSize,
                  color: 'white',
                }}
              >
                {site.priority}
              </Text>
            </Row>
          </Box>
          {site.currentState ? (
            <Box
              sx={{
                maxWidth: {
                  xs: `calc(100% - ${theme.spacing(10)})`,
                  sm: `calc(100% - ${theme.spacing(15)})`,
                },
                backgroundColor: theme.palette.success.main,
                borderRadius: theme.spacing(2.5),
                padding: `${theme.spacing(0.25)} ${theme.spacing(1)}`,
              }}
            >
              <Text
                sx={{
                  fontSize: theme.typography.body2.fontSize,
                  color: 'white',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  hyphens: 'auto',
                  lineHeight: 1.2,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {site.currentState}
              </Text>
            </Box>
          ) : (
            <></>
          )}
        </Row>
      </Box>
    </Box>
  );
};
